"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.convert = exports.Converter = void 0;
const model_1 = require("./model");
class Converter {
    constructor() {
        this.raw_input = null;
    }
    /**
     * Converting node that contains *mixed* _value property and we need to convert only specific type
     * for example below we have an array where items are of different typ, plus we have properties `attr` and `ds`
     * that contain the indexes or position in the _value array.
     *
     *  "_value": [{
     *      ....
     *       "eltype": "attr"
     *   }, {
     *      ...
     *       "eltype": "ds"
     *   }],
     *   "attr": [0],
     *   "ds": [1]
     *
     * if we need to convert only ds or only attr we use this function
     * @param from Source JSON node, the one we get from the API
     * @param node target object that we're going to populate
     * @param nameFrom Source JSON property which is array of indexes, in the example above it can be "attr" or "ds"
     * @param nameNode Target object property that is going to be populated from the source
     */
    convertNodeByRef(from, node, nameFrom, nameNode) {
        if (nameNode === undefined) {
            nameNode = nameFrom;
        }
        if (!from.hasOwnProperty(nameFrom)) {
            return;
        }
        if (!from.hasOwnProperty("_value")) {
            return;
        }
        if (!Array.isArray(from[nameFrom])) {
            return;
        }
        if (node.hasOwnProperty(nameNode) && !Array.isArray(node[nameNode])) {
            return;
        }
        if (!node.hasOwnProperty(nameNode)) {
            node[nameNode] = [];
        }
        for (const fromIdx of from[nameFrom]) {
            const result = this.convertNode(from['_value'][fromIdx]);
            node[nameNode].push(result);
        }
    }
    /**
     * Similar to {@link this.convertNodeByRef}, but uses property as is, without using array of indexes to traverse `_value` property
     * @param from Source JSON node, the one we get from the API
     * @param node target object that we're going to populate
     * @param nameFrom Source JSON property that will be converted
     * @param nameNode Target object property that is going to be populated from the source
     * @param convertSingletonList
     */
    convertNodeByProp(from, node, nameFrom, nameNode, convertSingletonList = false) {
        if (nameNode === undefined) {
            nameNode = nameFrom;
        }
        if (!from.hasOwnProperty(nameFrom)) {
            return;
        }
        const result = this.convertNode(from[nameFrom]);
        if (convertSingletonList) {
            if (Array.isArray(result) && result.length > 0) {
                node[nameNode] = result[0];
            }
        }
        else {
            node[nameNode] = result;
        }
    }
    /**
     * Convert function for {@link Ds}
     * @param node
     */
    convertDs(node) {
        const obj = new model_1.Ds();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "type");
        this.convertNodeByProp(node, obj, "out");
        this.convertNodeByProp(node, obj, "in");
        this.convertNodeByProp(node, obj, "matchrecognize");
        this.convertNodeByProp(node, obj, "tablesample");
        this.convertNodeByProp(node, obj, "filter");
        this.convertNodeByProp(node, obj, "agg");
        this.convertNodeByProp(node, obj, "vagg");
        this.convertNodeByProp(node, obj, "sort");
        this.convertNodeByProp(node, obj, "page");
        this.convertNodeByProp(node, obj, "setOp", "set_op");
        return obj;
    }
    /**
     * The main function that converts the whole ParSeQL JSON node to Tree of Data Objects
     * @param data
     */
    convert(data) {
        if (!data.hasOwnProperty("eltype")) {
            return null;
        }
        if (data['eltype'] != "parseql") {
            return null;
        }
        return this.convertParSeQL(data);
    }
    /**
     * Convert function for {@link Statement}
     * @param node
     */
    convertStatement(node) {
        const obj = new model_1.Statement();
        this.convertGenericProps(node, obj);
        if (node.hasOwnProperty("rawInput")) {
            this.raw_input = node['rawInput'];
        }
        if (node.hasOwnProperty("ds")) {
            for (let nodeElementKey in node['ds']) {
                const ds = this.convertDs(node['_value'][nodeElementKey]);
                obj.ds.push(ds);
            }
        }
        return obj;
    }
    /**
     * Convert function for {@link Out}
     * @param node
     */
    convertOut(node) {
        const obj = new model_1.Out();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "_value", "expr");
        return obj;
    }
    /**
     * Convert function for {@link Cast}
     * @param node
     */
    convertCast(node) {
        const obj = new model_1.Cast();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "data_type");
        this.convertNodeByProp(node, obj, "_value", "exp", true);
        return obj;
    }
    /**
     * Convert function for {@link Const}
     * @param node
     */
    convertConst(node) {
        const obj = new model_1.Const();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "value");
        return obj;
    }
    /**
     * Convert function for {@link Attr}
     * @param node
     */
    convertAttr(node) {
        const obj = new model_1.Attr();
        this.convertGenericProps(node, obj);
        return obj;
    }
    convertAsterisk(node) {
        const obj = new model_1.Asterisk();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "_value", "expr");
        return obj;
    }
    /**
     * Convert function for {@link In}
     * @param node
     */
    convertIn(node) {
        const obj = new model_1.In();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "_value", "expr");
        return obj;
    }
    /**
     * Convert function for {@link Case}
     * @param node
     */
    convertCase(node) {
        const obj = new model_1.Case();
        this.convertGenericProps(node, obj);
        this.convertNodeByRef(node, obj, "when");
        this.convertNodeByRef(node, obj, "else");
        this.convertNodeByRef(node, obj, "expr");
        return obj;
    }
    /**
     * Convert function for {@link When}
     * @param node
     */
    convertWhen(node) {
        const obj = new model_1.When();
        this.convertGenericProps(node, obj);
        this.convertNodeByRef(node, obj, "then");
        this.convertNodeByRef(node, obj, "expr");
        return obj;
    }
    /**
     * Convert function for {@link Filter}
     * @param node
     */
    convertFilter(node) {
        const obj = new model_1.Filter();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "op");
        return obj;
    }
    /**
     * Convert function for {@link Vfilter}
     * @param node
     */
    convertVFilter(node) {
        const obj = new model_1.Vfilter();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "_value", "expr", true);
        return obj;
    }
    /**
     * Convert function for {@link Func}
     * @param node
     */
    convertFunc(node) {
        const obj = new model_1.Func();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "type", "func_type");
        this.convertNodeByProp(node, obj, "withinGroup", "within_group");
        this.convertNodeByProp(node, obj, "_value", "expr");
        return obj;
    }
    /**
     * Convert function for {@link WFunc}
     * @param node
     */
    convertWFunc(node) {
        const obj = new model_1.WFunc();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "name");
        this.convertNodeByProp(node, obj, "type", "func_type");
        this.convertNodeByRef(node, obj, "expr");
        this.convertNodeByRef(node, obj, "sort");
        this.convertNodeByRef(node, obj, "frame");
        this.convertNodeByRef(node, obj, "partition");
        return obj;
    }
    /**
     * Convert function for {@link Op}
     * @param node
     */
    convertOp(node) {
        const obj = new model_1.Op();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "type");
        this.convertNodeByProp(node, obj, "_value", "expr");
        return obj;
    }
    /**
     * Convert function for {@link Page}
     * @param node
     */
    convertPage(node) {
        const obj = new model_1.Page();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "type");
        this.convertNodeByProp(node, obj, "value");
        return obj;
    }
    /**
     * Convert function for {@link Join}
     * @param node
     */
    convertJoin(node) {
        const obj = new model_1.Join();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "type");
        this.convertNodeByProp(node, obj, "subType", "sub_type");
        this.convertNodeByProp(node, obj, "op");
        this.convertNodeByProp(node, obj, "ds");
        return obj;
    }
    /**
     * Convert function for {@link Else}
     * @param node
     */
    convertElse(node) {
        const obj = new model_1.Else();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "_value", "expr", true);
        return obj;
    }
    /**
     * Convert function for {@link Then}
     * @param node
     */
    convertThen(node) {
        const obj = new model_1.Then();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "_value", "expr", true);
        return obj;
    }
    /**
     * Convert function for {@link Agg}
     * @param node
     */
    convertAgg(node) {
        const obj = new model_1.Agg();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "_value", "expr");
        this.convertNodeByProp(node, obj, "filter");
        return obj;
    }
    /**
     * Convert function for {@link Vagg}
     * @param node
     */
    convertVAgg(node) {
        const obj = new model_1.Vagg();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "_value", "expr", true);
        this.convertNodeByProp(node, obj, "vfilter");
        return obj;
    }
    /**
     * Convert function for {@link Sort}
     * @param node
     */
    convertSort(node) {
        const obj = new model_1.Sort();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "_value", "expr");
        return obj;
    }
    /**
     * Convert function for {@link StructRef}
     * @param node
     */
    convertStructRef(node) {
        const obj = new model_1.StructRef();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "columnRef", "column_ref");
        this.convertNodeByProp(node, obj, "structureRef", "structure_ref");
        return obj;
    }
    /**
     * Convert function for {@link TableFunc}
     * @param node
     */
    convertTableFunc(node) {
        const obj = new model_1.TableFunc();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "type");
        this.convertNodeByProp(node, obj, "funcName", "func_name");
        this.convertNodeByProp(node, obj, "names");
        this.convertNodeByProp(node, obj, "args");
        this.convertNodeByProp(node, obj, "partition");
        this.convertNodeByProp(node, obj, "sort");
        this.convertNodeByProp(node, obj, "frame");
        this.convertNodeByProp(node, obj, "subQuery", "sub_query");
        return obj;
    }
    /**
     * Convert function for {@link TableSample}
     * @param node
     */
    convertTableSample(node) {
        const obj = new model_1.TableSample();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "type");
        this.convertNodeByProp(node, obj, "funcName", "func_name");
        this.convertNodeByProp(node, obj, "value");
        this.convertNodeByProp(node, obj, "seed");
        this.convertNodeByProp(node, obj, "seed_type");
        this.convertNodeByProp(node, obj, "num");
        this.convertNodeByProp(node, obj, "probability");
        this.convertNodeByProp(node, obj, "sampleMethod", "sample_method");
        this.convertNodeByProp(node, obj, "sampleType", "sample_type");
        return obj;
    }
    convertError(node) {
        const obj = new Error();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "message");
        return obj;
    }
    /**
     * Converts the most commons and simple (string only) properties
     * @param node Source JSON node, the one we get from the API
     * @param obj target object that we're going to populate
     */
    convertGenericProps(node, obj) {
        this.convertNodeByProp(node, obj, "name");
        this.convertNodeByProp(node, obj, "alias");
        this.convertNodeByProp(node, obj, "direction");
        this.convertNodeByProp(node, obj, "refTo");
        this.convertNodeByProp(node, obj, "pos");
        if (node.hasOwnProperty("pos")) {
            obj.raw_input = this.get_raw_input_by_id(node['pos']);
        }
        this.convertNodeByProp(node, obj, "refvar");
        this.convertNodeByProp(node, obj, "refatt");
        this.convertNodeByProp(node, obj, "refds");
        this.convertNodeByProp(node, obj, "refdb");
        this.convertNodeByProp(node, obj, "refsch");
        this.convertNodeByProp(node, obj, "fullref");
        this.convertNodeByProp(node, obj, "refoutidx");
    }
    /**
     * Convert function for {@link ParSeQL}
     * @param node
     */
    convertParSeQL(node) {
        const obj = new model_1.ParSeQL();
        this.convertGenericProps(node, obj);
        this.convertNodeByProp(node, obj, "version");
        this.convertNodeByProp(node, obj, "status");
        this.convertNodeByProp(node, obj, "ts");
        this.convertNodeByRef(node, obj, "statement");
        this.convertNodeByRef(node, obj, "error");
        return obj;
    }
    /**
     * Converter dispatcher, applies checks on the input JSON nodes and call the right converter based on the node's type.
     * @param node
     * @param parent
     */
    convertNode(node, parent) {
        if (node == null) {
            return null;
        }
        // for string/terminal nodes - just return them
        if (typeof node == "string") {
            return node;
        }
        // for arrays go through each child
        if (Array.isArray(node)) {
            const items = [];
            // and return array of convertor results
            for (const hasValue of node) {
                const result = this.convertNode(hasValue, parent);
                items.push(result);
            }
            return items;
        }
        // for objects we check if we know type of the object
        // and if it has listed values
        const isTypeKnown = node.hasOwnProperty("eltype");
        if (isTypeKnown) {
            switch (node['eltype']) {
                case "parseql":
                    return this.convertParSeQL(node);
                case "statement":
                    return this.convertStatement(node);
                case "ds":
                    return this.convertDs(node);
                case "out":
                    return this.convertOut(node);
                case "in":
                    return this.convertIn(node);
                case "cast":
                    return this.convertCast(node);
                case "case":
                    return this.convertCase(node);
                case "else":
                    return this.convertElse(node);
                case "then":
                    return this.convertThen(node);
                case "when":
                    return this.convertWhen(node);
                case "filter":
                    return this.convertFilter(node);
                case "vfilter":
                    return this.convertVFilter(node);
                case "func":
                    return this.convertFunc(node);
                case "wfunc":
                    return this.convertWFunc(node);
                case "op":
                    return this.convertOp(node);
                case "page":
                    return this.convertPage(node);
                case "const":
                    return this.convertConst(node);
                case "attr":
                    return this.convertAttr(node);
                case "asterisk":
                    return this.convertAsterisk(node);
                case "join":
                    return this.convertJoin(node);
                case "agg":
                    return this.convertAgg(node);
                case "vagg":
                    return this.convertVAgg(node);
                case "sort":
                    return this.convertSort(node);
                case "structref":
                    return this.convertStructRef(node);
                case "tablefunc":
                    return this.convertTableFunc(node);
                case "tablesample":
                    return this.convertTableSample(node);
                case "error":
                    return this.convertError(node);
                default:
                    throw new Error("Invalid type " + node['eltype']);
            }
        }
        else {
            throw new Error("Invalid type null");
        }
    }
    get_raw_input_by_id(_id) {
        if (this.raw_input === null) {
            return "";
        }
        //extract positions from id
        const pos = _id.split("-");
        if (pos.length != 2) {
            return "";
        }
        const start = pos[0];
        const end = pos[1];
        return this.raw_input.substring(start, end);
    }
}
exports.Converter = Converter;
function convert(data) {
    if (!data.hasOwnProperty("eltype")) {
        return null;
    }
    if (data['eltype'] != "parseql") {
        return null;
    }
    const converter = new Converter();
    return converter.convert(data);
}
exports.convert = convert;
